import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useAuthStore, getAuthToken, type InAppNotification } from '../store';
import { useOrganizationStore } from '../store/organizationStore';

// Résolution dynamique de l'URL WebSocket :
// 1. VITE_WS_URL explicite (recommandé en production/staging)
// 2. Dérivé de VITE_API_URL (supprime /api à la fin)
// 3. Fallback localhost développement
const resolveWsUrl = (): string => {
  const explicit = import.meta.env.VITE_WS_URL as string | undefined;
  if (explicit) return explicit;
  const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
  if (apiUrl) return apiUrl.replace(/\/api\/?$/, '');
  return 'http://localhost:4000';
};

const WS_URL = resolveWsUrl();

/**
 * Ouvre une connexion Socket.IO sur le namespace /project et écoute
 * l'événement `notification:received` pour alimenter le store en temps réel.
 *
 * Ce hook est monté une seule fois dans App.tsx dès que l'utilisateur est
 * authentifié ; il se déconnecte proprement à la déconnexion.
 *
 * Lorsqu'une notification ORG_JOIN_REQUEST arrive, le panneau organisation
 * est automatiquement rechargé pour que le gestionnaire voie les nouvelles
 * demandes sans avoir à cliquer manuellement sur "Rafraîchir".
 */
export const useNotificationSocket = (): void => {
  const user                    = useAuthStore((s) => s.user);
  const addSocketNotification   = useAuthStore((s) => s.addSocketNotification);
  const fetchNotifications      = useAuthStore((s) => s.fetchNotifications);
  const socketRef               = useRef<Socket | null>(null);

  useEffect(() => {
    if (!user) {
      // Déconnecter si l'utilisateur se déconnecte
      socketRef.current?.disconnect();
      socketRef.current = null;
      return;
    }

    // Récupérer les notifications existantes au login
    void fetchNotifications();

    // Créer la connexion Socket.IO sur le namespace /project
    // (même namespace que la collaboration — le serveur crée la room user:{id})
    const token = getAuthToken() ?? undefined;

    const socket = io(`${WS_URL}/project`, {
      withCredentials: true,
      auth: token ? { token } : undefined,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    socket.on('notification:received', (n: InAppNotification) => {
      addSocketNotification(n);

      // Si une demande d'adhésion est reçue et que l'utilisateur est manager,
      // recharger automatiquement les données de l'organisation.
      if (n.type === 'ORG_JOIN_REQUEST') {
        const orgId = useAuthStore.getState().user?.organizationId;
        if (orgId) {
          void useOrganizationStore.getState().fetchOrg(orgId);
        }
      }
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
};
